<template>
  <div
    class="coin-root"
    :class="{ 'coin-with-shadow': shadow }"
    :style="rootStyle"
  >
    <div
      v-if="shadow"
      class="coin-ground-shadow"
      :style="shadowStyle"
    />
    <div
      class="spinningasset coin"
      :style="coinWrapperStyle"
    >
      <div class="coin-inner" :style="coinInnerStyle">
        <div class="coin-face-back" />
        <i v-for="n in 11" :key="n" class="coin-edge-i" />
        <em class="coin-edge-em coin-edge-em-1" />
        <em class="coin-edge-em coin-edge-em-2" />
        <div class="coin-face-front" />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, onMounted, onBeforeUnmount } from 'vue'

const props = withDefaults(
  defineProps<{
    size?: number
    duration?: number
    paused?: boolean
    /** When true, shows a ground shadow that animates with the coin rotation (elongated when edge-on, round when face-on). */
    shadow?: boolean
  }>(),
  {
    size: 100,
    duration: 2.5,
    paused: false,
    shadow: false,
  }
)

// Respect the OS "reduce motion" setting: the coin is a continuously spinning
// decoration, so under reduced motion we hold it face-front (static) instead of
// looping. This also stops the two infinite animations from burning CPU.
const reduceMotion = ref(false)
let motionQuery: MediaQueryList | null = null
const syncMotion = (e: MediaQueryListEvent | MediaQueryList) => { reduceMotion.value = e.matches }
onMounted(() => {
  if (typeof window === 'undefined' || !window.matchMedia) return
  motionQuery = window.matchMedia('(prefers-reduced-motion: reduce)')
  reduceMotion.value = motionQuery.matches
  motionQuery.addEventListener?.('change', syncMotion)
})
onBeforeUnmount(() => motionQuery?.removeEventListener?.('change', syncMotion))

// Paused when the caller asks OR the user prefers reduced motion.
const playState = computed(() => (props.paused || reduceMotion.value ? 'paused' : 'running'))

const rootStyle = computed(() => ({
  paddingBottom: props.shadow ? `${Math.round(props.size * 0.2)}px` : undefined,
}))

const coinWrapperStyle = computed(() => ({
  width: `${props.size}px`,
  height: `${props.size}px`,
  animation: reduceMotion.value ? 'none' : `coin-brightness ${props.duration}s infinite linear`,
  animationPlayState: playState.value,
  '--coin-step': `${(props.size * 0.01).toFixed(3)}px`,
}))

const coinInnerStyle = computed(() => ({
  width: `${props.size}px`,
  height: `${props.size}px`,
  animation: reduceMotion.value ? 'none' : `coin-spin ${props.duration}s infinite linear`,
  animationPlayState: playState.value,
}))

const shadowStyle = computed(() => ({
  width: `${props.size}px`,
  height: `${Math.max(8, Math.round(props.size * 0.22))}px`,
  animation: reduceMotion.value ? 'none' : `coin-shadow-sync ${props.duration}s infinite linear`,
  animationPlayState: playState.value,
}))
</script>
